import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { X, Check, XCircle, ChevronUp, ChevronDown } from 'lucide-react';
import ApiService from '../../components/api_services/apiService.jsx';
import {
  setShowDetailView,
  setSelectedArticle,
  setDetailViewTab,
  setShowEditModal,
} from '../../store/encyclopediaTabSlice';
import './Encyclopedia.css';

function ArticlePhoto({ photoPath }) {
  const normalizedPhotoPath = String(photoPath || '')
    .trim()
    .replace(/^https?:\/\/[^/]+\/images\//i, '')
    .replace(/^\/?images\//i, '')
    .split('?')[0]
    .split('#')[0];
  const encodedHash = encodeURIComponent(normalizedPhotoPath);

  const candidates = /^https?:\/\//i.test(String(photoPath || '').trim())
    ? [String(photoPath).trim()]
    : [
        `https://api-preprod.drivelog365.com/images/${encodedHash}`,
        `${(process.env.REACT_APP_API_URL || 'https://api-dev.drivelog365.com').replace(/\/$/, '')}/images/${encodedHash}`,
      ];

  const [srcIndex, setSrcIndex] = useState(0);

  const handleError = () => {
    if (srcIndex < candidates.length - 1) {
      setSrcIndex((prev) => prev + 1);
    }
  };

  return (
    <img
      src={candidates[srcIndex]}
      alt="Zdjęcie artykułu"
      loading="lazy"
      onError={handleError}
    />
  );
}

function ArticleDetailView({ articlesList = [], currentIndex = 0 }) {
  const dispatch = useDispatch();
  const selectedArticle = useSelector((state) => state.encyclopedia.selectedArticle);
  const detailViewTab = useSelector((state) => state.encyclopedia.detailViewTab);
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Calculate navigation info
  const currentPos = currentIndex + 1;
  const totalItems = articlesList.length;

  useEffect(() => {
    if (selectedArticle?.id) {
      loadArticle();
    }
  }, [selectedArticle]);

  const loadArticle = async () => {
    if (!selectedArticle?.id) return;
    setLoading(true);
    try {
      const data = await ApiService.getArticleById(selectedArticle.id);
      setArticle(data);
    } catch (error) {
      console.error('Błąd podczas ładowania artykułu:', error);
      alert('Nie udało się załadować szczegółów artykułu.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    dispatch(setShowDetailView(false));
    dispatch(setSelectedArticle(null));
  };

  const handleAccept = async () => {
    if (!article?.id) return;
    try {
      await ApiService.acceptArticle(article.id);
      alert('Artykuł został zaakceptowany.');
      loadArticle();
      // Reload list - would need to trigger parent refresh
    } catch (error) {
      console.error('Błąd podczas akceptacji:', error);
      alert('Nie udało się zaakceptować artykułu.');
    }
  };

  const handleReject = async () => {
    if (!article?.id) return;
    if (!window.confirm('Czy na pewno chcesz odrzucić ten artykuł?')) {
      return;
    }
    try {
      await ApiService.rejectArticle(article.id);
      alert('Artykuł został odrzucony.');
      loadArticle();
    } catch (error) {
      console.error('Błąd podczas odrzucenia:', error);
      alert('Nie udało się odrzucić artykułu.');
    }
  };

  const handleEdit = () => {
    dispatch(setShowEditModal(true));
  };

  const handleDelete = async () => {
    if (!article?.id) return;
    if (!window.confirm('Czy na pewno chcesz usunąć ten artykuł? Ta operacja jest nieodwracalna.')) {
      return;
    }
    try {
      await ApiService.deleteArticle(article.id);
      alert('Artykuł został usunięty.');
      handleClose();
      // Reload list - would need to trigger parent refresh
    } catch (error) {
      console.error('Błąd podczas usuwania:', error);
      alert('Nie udało się usunąć artykułu.');
    }
  };

  if (loading) {
    return (
      <div className="detail-view-overlay">
        <div className="detail-view">
          <div className="detail-view-loading">Ładowanie...</div>
        </div>
      </div>
    );
  }

  if (!article) {
    return null;
  }

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toISOString().split('T')[0];
    } catch (e) {
      return dateString;
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'PENDING':
        return 'do weryfikacji';
      case 'APPROVED':
        return 'aktywny';
      case 'REJECTED':
        return 'odrzucony';
      default:
        return status;
    }
  };

  const getStatusClass = (status) => {
    switch (status) {
      case 'PENDING':
        return 'status-pending';
      case 'APPROVED':
        return 'status-approved';
      case 'REJECTED':
        return 'status-rejected';
      default:
        return '';
    }
  };

  const getArticlePhotoPaths = () => {
    if (!article) return [];
    if (Array.isArray(article.photo_paths)) return article.photo_paths;
    if (Array.isArray(article.photoPaths)) return article.photoPaths;
    return [];
  };

  const handlePrevious = () => {
    if (currentIndex > 0 && articlesList[currentIndex - 1]) {
      dispatch(setSelectedArticle({ id: articlesList[currentIndex - 1].id }));
    }
  };

  const handleNext = () => {
    if (currentIndex < articlesList.length - 1 && articlesList[currentIndex + 1]) {
      dispatch(setSelectedArticle({ id: articlesList[currentIndex + 1].id }));
    }
  };

  const isApproved = article?.status === 'APPROVED';

  return (
    <div className="detail-view-overlay" onClick={handleClose}>
      <div className="detail-view" onClick={(e) => e.stopPropagation()}>
        <div className="detail-view-top-bar">
          <button className="detail-view-close" onClick={handleClose}>
            <X size={20} />
          </button>
          <div className="detail-view-navigation">
            <button 
              className="detail-view-nav-btn" 
              onClick={handlePrevious}
              disabled={currentIndex === 0}
            >
              <ChevronUp size={16} />
            </button>
            <span className="detail-view-nav-info">
              {currentPos} z {totalItems} wyników
            </span>
            <button 
              className="detail-view-nav-btn" 
              onClick={handleNext}
              disabled={currentIndex >= articlesList.length - 1}
            >
              <ChevronDown size={16} />
            </button>
          </div>
          <div className="detail-view-date-info">
            Data utworzenia: {formatDate(article.createdAt)}
          </div>
        </div>

        <div className="detail-view-header">
          <div className="detail-view-header-left">
            <div className="detail-view-meta">
              (ID {article.id}) Ostatnia aktualizacja {formatDate(article.updatedAt)}
            </div>
            <div className="detail-view-title-row">
              <h2 className="detail-view-title">{article.title}</h2>
              <span className={`detail-view-status ${getStatusClass(article.status)}`}>
                {getStatusLabel(article.status)}
              </span>
            </div>
          </div>
          <div className="detail-view-header-right">
            {!isApproved && (
              <button className="btn-accept" onClick={handleAccept}>
                <Check size={16} />
                Zaakceptuj zmiany
              </button>
            )}
            <button className="btn-reject" onClick={handleReject}>
              <XCircle size={16} />
              Odrzuć
            </button>
          </div>
        </div>

        <div className="detail-view-tabs">
          <button
            className={`detail-view-tab ${detailViewTab === 'Dane podstawowe' ? 'is-active' : ''}`}
            onClick={() => dispatch(setDetailViewTab('Dane podstawowe'))}
          >
            Dane podstawowe
          </button>
          <button
            className={`detail-view-tab ${detailViewTab === 'Opis' ? 'is-active' : ''}`}
            onClick={() => dispatch(setDetailViewTab('Opis'))}
          >
            Opis
          </button>
        </div>

        <div className="detail-view-content">
          {detailViewTab === 'Dane podstawowe' ? (
            <div className="detail-view-basic-data">
              <div className="detail-view-field">
                <label>ID</label>
                <div>{article.id}</div>
              </div>
              <div className="detail-view-field">
                <label>Autor</label>
                <div>{article.author_name || article.authorName}</div>
              </div>
              <div className="detail-view-field">
                <label>Adres</label>
                <div>
                  <input
                    type="text"
                    value={article.street || ''}
                    readOnly
                    className="detail-view-input-readonly"
                  />
                  <input
                    type="text"
                    value={article.street_number || ''}
                    readOnly
                    className="detail-view-input-readonly"
                  />
                  <input
                    type="text"
                    value={article.post_code || ''}
                    readOnly
                    className="detail-view-input-readonly"
                  />
                  <input
                    type="text"
                    value={article.city || ''}
                    readOnly
                    className="detail-view-input-readonly"
                  />
                </div>
              </div>
              <div className="detail-view-field">
                <label>Pineska google</label>
                <div>
                  <a href={article.pin_url} target="_blank" rel="noopener noreferrer">
                    {article.pin_url}
                  </a>
                </div>
              </div>
              <div className="detail-view-field">
                <label>Zdjęcia</label>
                <div className="detail-view-photos">
                  {getArticlePhotoPaths().length > 0 ? (
                    getArticlePhotoPaths().map((photoPath) => (
                      <div key={photoPath} className="detail-view-photo-placeholder">
                        <ArticlePhoto photoPath={photoPath} />
                      </div>
                    ))
                  ) : (
                    <div className="detail-view-photo-placeholder">Brak zdjęć</div>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="detail-view-description">
              <label>Opis</label>
              <div className="detail-view-description-text">
                {article.description || 'Brak opisu'}
              </div>
            </div>
          )}
        </div>

        <div className="detail-view-footer">
          <button className="btn-delete" onClick={handleDelete}>
            <X size={16} />
            Usuń
          </button>
          <button className="btn-edit" onClick={handleEdit}>
            Edytuj
          </button>
        </div>
      </div>
    </div>
  );
}

export default ArticleDetailView;

